
// audio.js — AudioEngine: procedural Web Audio synthesis for every table sound.
//
// No samples are loaded. Each sound is assembled on demand from oscillator voices and slices of
// one shared white-noise buffer, each shaped by a three-stage gain envelope, then summed through a
// master gain into a dynamics compressor so stacked hits (multiball + pops + slings) do not clip.
// main.js constructs one AudioEngine, calls ensure()/resume() on the first pointer/key gesture
// (browsers keep an AudioContext suspended until user input), setMuted(bool) from the mute button,
// and play(name, params) for every { type: 'sound' } fx entry that Game.sound() pushes.
//
// Determinism: noise() draws two Math.random() values (playback rate, then start offset), 'spin'
// draws one for its pitch, and ensure() draws one per noise-buffer sample. Order and count are kept
// exactly as in the original build for the original sounds; 'rampEnter' and 'ballHit' (added in the
// improvement pass) each draw two via noise(). Tests mute the engine, so none of this affects physics.

class AudioEngine {
  constructor() {
    this.ctx = null; // AudioContext, created lazily by ensure()
    this.enabled = true; // false while muted (setMuted)
    this.master = null; // DynamicsCompressorNode feeding ctx.destination
    this.gain = null; // master GainNode that every voice envelope connects to
    this.noiseBuf = null; // 1.2 s of white noise shared by every noise() voice
  }

  /** Lazily create the AudioContext and master chain; false when Web Audio is unavailable. */
  ensure() {
    if (this.ctx) return true;
    try {
      this.ctx = new (window.AudioContext || window.webkitAudioContext)();
      // Master chain: voices -> gain (0.85) -> compressor (-14 dB threshold, 6:1) -> speakers.
      this.master = this.ctx.createDynamicsCompressor();
      this.master.threshold.value = -14;
      this.master.ratio.value = 6;
      this.gain = this.ctx.createGain();
      this.gain.gain.value = 0.85;
      this.gain.connect(this.master);
      this.master.connect(this.ctx.destination);
      // Shared white-noise buffer (1.2 s); noise() plays random slices of it at random rates.
      const sampleCount = this.ctx.sampleRate * 1.2;
      this.noiseBuf = this.ctx.createBuffer(1, sampleCount, this.ctx.sampleRate);
      const samples = this.noiseBuf.getChannelData(0);
      for (let i = 0; i < sampleCount; i++) samples[i] = Math.random() * 2 - 1;
      return true;
    } catch {
      return false;
    }
  }

  /** Resume a context the browser auto-suspended before the first user gesture. */
  resume() {
    this.ctx?.resume?.();
  }

  /** Mute/unmute. A muted engine drops play() calls entirely; nothing is scheduled. */
  setMuted(muted) {
    this.enabled = !muted;
  }

  /**
   * Attack/decay gain envelope: 1e-4 -> `peak` linearly over `attack` s, then an exponential fall
   * to `floor` over `decay` s. Wires source -> envelope -> master gain and returns the GainNode.
   */
  env(source, t0, attack, peak, decay, floor = 1e-4) {
    const envelope = this.ctx.createGain();
    envelope.gain.setValueAtTime(1e-4, t0);
    envelope.gain.linearRampToValueAtTime(peak, t0 + attack);
    // An exponential ramp cannot reach 0, so the floor is clamped to a tiny positive value.
    envelope.gain.exponentialRampToValueAtTime(Math.max(floor, 1e-4), t0 + attack + decay);
    source.connect(envelope);
    envelope.connect(this.gain);
    return envelope;
  }

  /**
   * One oscillator voice: a `type` wave at `freq` Hz from t0, optionally gliding to `glideTo` Hz
   * (clamped to >= 20 Hz) over `dur` s, shaped by an `attack`-then-`dur` envelope.
   */
  osc(type, freq, t0, dur, peak = 0.2, glideTo = null, attack = 0.004) {
    const oscillator = this.ctx.createOscillator();
    oscillator.type = type;
    oscillator.frequency.setValueAtTime(freq, t0);
    if (glideTo !== null) {
      oscillator.frequency.exponentialRampToValueAtTime(Math.max(20, glideTo), t0 + dur);
    }
    this.env(oscillator, t0, attack, peak, dur);
    oscillator.start(t0);
    oscillator.stop(t0 + attack + dur + 0.05);
    return oscillator;
  }

  /**
   * Filtered noise burst from the shared buffer. The playback rate (0.8-1.2x) and start offset
   * (0-0.4 s) are randomised so repeated hits never sound identical; a `filterType` biquad at
   * `freq` Hz with resonance `q` optionally sweeps to `glideTo` Hz (>= 40) over `dur` s.
   */
  noise(t0, dur, peak, filterType = 'bandpass', freq = 1e3, q = 1, glideTo = null) {
    const source = this.ctx.createBufferSource();
    source.buffer = this.noiseBuf;
    source.playbackRate.value = 0.8 + Math.random() * 0.4; // first Math.random() of this voice
    const filter = this.ctx.createBiquadFilter();
    filter.type = filterType;
    filter.frequency.setValueAtTime(freq, t0);
    filter.Q.value = q;
    if (glideTo !== null) {
      filter.frequency.exponentialRampToValueAtTime(Math.max(40, glideTo), t0 + dur);
    }
    source.connect(filter);
    this.env(filter, t0, 0.002, peak, dur);
    source.start(t0, Math.random() * 0.4); // second Math.random(): random slice of the buffer
    source.stop(t0 + dur + 0.08);
  }

  /** Pitch-glide voice: a `type` wave sweeping fromHz -> toHz over `dur` s. */
  chirp(fromHz, toHz, t0, dur, peak = 0.15, type = 'sine') {
    this.osc(type, fromHz, t0, dur, peak, toHz);
  }

  /**
   * Arpeggio starting now: each note begins `step` s after the previous one, as a `type` voice
   * doubled by a quieter (0.4x), shorter (0.7x) triangle an octave up for sparkle.
   */
  arp(notes, step, dur, peak, type = 'square') {
    const now = this.ctx.currentTime;
    notes.forEach((hz, i) => {
      this.osc(type, hz, now + i * step, dur, peak);
      this.osc('triangle', hz * 2, now + i * step, dur * 0.7, peak * 0.4);
    });
  }

  /**
   * Schedule the named sound. `params` carries the extra fields Game.sound() attached:
   * vel (0-1 impact strength for clack/rubber/ballHit), n (pop bumper index / combo chain length),
   * power (0-1 plunge strength). Silently ignored while muted, without Web Audio, or while the
   * context is still suspended (before the first user gesture).
   */
  play(name, params = {}) {
    if (!this.enabled || !this.ensure() || this.ctx.state === 'suspended') return;
    const now = this.ctx.currentTime;
    const velocity = params.vel ?? 1;
    switch (name) {
      case 'flipUp':
        // Flipper solenoid thunk: low-passed noise slap over a 64->48 Hz square knock.
        this.noise(now, 0.045, 0.5, 'lowpass', 900, 1);
        this.osc('square', 64, now, 0.05, 0.3, 48);
        break;
      case 'flipDown':
        // Softer clunk of the flipper dropping back to rest.
        this.noise(now, 0.035, 0.22, 'lowpass', 500, 1);
        break;
      case 'sling':
        // Slingshot kicker: sharp band-passed snap with a 190->120 Hz triangle thud.
        this.noise(now, 0.05, 0.5, 'bandpass', 850, 3);
        this.osc('triangle', 190, now, 0.06, 0.35, 120);
        break;
      case 'pop':
        // Pop bumper: deep 165->88 Hz sine thump, a bright ping pitched per bumper index, and a hiss.
        this.osc('sine', 165, now, 0.09, 0.5, 88);
        this.osc('triangle', 2300 + (params.n || 0) * 180, now, 0.05, 0.16, 1600);
        this.noise(now, 0.03, 0.25, 'highpass', 2500, 1);
        break;
      case 'drop':
        // Drop target falling: metallic band-passed click with a low 120->80 Hz square knock.
        this.noise(now, 0.05, 0.42, 'bandpass', 1900, 2);
        this.osc('square', 120, now, 0.05, 0.2, 80);
        break;
      case 'dropReset':
        // Drop bank reset: heavier, longer mechanical thud as the targets pop back up.
        this.noise(now, 0.09, 0.3, 'lowpass', 700, 1);
        this.osc('square', 70, now, 0.09, 0.25, 55);
        break;
      case 'scoopIn':
        // Ball swallowed by the scoop: falling 500->130 Hz triangle plus a muffled landing thump.
        this.chirp(500, 130, now, 0.16, 0.3, 'triangle');
        this.noise(now + 0.05, 0.08, 0.3, 'lowpass', 500, 1);
        break;
      case 'scoopEject':
        // Scoop kicker firing: low noise punch and a rising 140->420 Hz square zip.
        this.noise(now, 0.06, 0.5, 'lowpass', 1100, 1);
        this.chirp(140, 420, now, 0.09, 0.3, 'square');
        break;
      case 'launch': {
        // Plunger release: noise whoosh whose length and brightness grow with plunge power, over a
        // 90->55 Hz square spring twang.
        const power = params.power ?? 1;
        this.noise(now, 0.1 + power * 0.06, 0.5, 'lowpass', 600 + power * 900, 1);
        this.chirp(90, 55, now, 0.14, 0.42, 'square');
        break;
      }
      case 'kickback':
        // Kickback firing: rising 300->1500 Hz sawtooth zap with a bright noise crack.
        this.chirp(300, 1500, now, 0.12, 0.3, 'sawtooth');
        this.noise(now, 0.07, 0.4, 'highpass', 1300, 2);
        break;
      case 'knocker':
        // Replay knocker: loud high band-passed crack plus a 95->70 Hz square body.
        this.noise(now, 0.05, 0.9, 'bandpass', 3200, 1.5);
        this.osc('square', 95, now, 0.04, 0.5, 70);
        break;
      case 'nudge':
        // Cabinet nudge: dull low-passed thump.
        this.noise(now, 0.05, 0.3, 'lowpass', 300, 1);
        break;
      case 'clack':
        // Ball hitting metal: short bright click scaled by impact velocity.
        this.noise(now, 0.025, 0.16 * velocity, 'bandpass', 2600, 2);
        break;
      case 'rubber':
        // Ball hitting rubber: muted low thud scaled by impact velocity.
        this.noise(now, 0.03, 0.2 * velocity, 'lowpass', 900, 1);
        break;
      case 'rollover':
        // Rollover lane switch: high sine blip (1318->1200 Hz) with a faint octave sparkle.
        this.osc('sine', 1318, now, 0.09, 0.16, 1200);
        this.osc('sine', 2637, now, 0.05, 0.07);
        break;
      case 'spin':
        // Spinner tick: tiny square click at a random 700-1000 Hz pitch per revolution.
        this.osc('square', 700 + Math.random() * 300, now, 0.025, 0.1);
        break;
      case 'orbit':
        // Orbit shot made: quick rising three-note triangle arpeggio.
        this.arp([659, 880, 1174], 0.05, 0.1, 0.15, 'triangle');
        break;
      case 'ramp':
        // Ramp shot made (no combo): two-note rising triangle chime.
        this.arp([523, 784], 0.06, 0.14, 0.17, 'triangle');
        break;
      case 'combo':
        // Ramp combo: square arpeggio that grows from 2 to 4 notes with the combo chain length.
        this.arp([523, 659, 784, 1046].slice(0, 2 + Math.min(2, params.n || 0)), 0.05, 0.12, 0.2);
        break;
      case 'standup':
        // Stand-up target: short 440->330 Hz square blip.
        this.osc('square', 440, now, 0.06, 0.16, 330);
        break;
      case 'outlane':
        // Outlane entry: sad falling 400->180 Hz triangle slide.
        this.chirp(400, 180, now, 0.25, 0.2, 'triangle');
        break;
      case 'startGame':
        // Game start: rising four-note square fanfare followed by a soft noise swell.
        this.arp([392, 523, 659, 784], 0.08, 0.16, 0.2);
        this.noise(now + 0.3, 0.2, 0.12, 'bandpass', 900, 1);
        break;
      case 'bankComplete':
        // Drop bank completed: three-note rising square chime.
        this.arp([659, 784, 988], 0.07, 0.18, 0.22);
        break;
      case 'lock':
        // Ball locked: rising sawtooth arpeggio over a long 65->60 Hz sub-bass drone.
        this.arp([262, 330, 392, 523], 0.06, 0.3, 0.22, 'sawtooth');
        this.osc('sine', 65, now, 0.4, 0.3, 60);
        break;
      case 'multiball':
        // Multiball start: six-note sawtooth fanfare, a long band-pass noise sweep falling to
        // 300 Hz, and a 0.9 s sub-bass rumble.
        this.arp([392, 523, 659, 784, 1046, 1318], 0.07, 0.35, 0.2, 'sawtooth');
        this.noise(now + 0.42, 0.5, 0.2, 'bandpass', 1400, 0.8, 300);
        this.osc('sine', 55, now, 0.9, 0.3, 48);
        break;
      case 'jackpot':
        // Jackpot: bright four-note square fanfare with a 62->55 Hz sub-bass hit underneath.
        this.arp([784, 988, 1174, 1568], 0.08, 0.4, 0.24, 'square');
        this.osc('sine', 62, now, 0.5, 0.3, 55);
        break;
      case 'skillshot':
        // Skill shot: sparkling high four-note triangle run.
        this.arp([1046, 1318, 1568, 2093], 0.06, 0.3, 0.2, 'triangle');
        break;
      case 'tankstick':
        // 5x ramp combo ("TANKSTICK! 2,000,000"): five-note sawtooth fanfare.
        this.arp([523, 659, 784, 1046, 1318], 0.09, 0.5, 0.22, 'sawtooth');
        break;
      case 'frenzy':
        // Frenzy start: four rising square sirens, each pitched higher and 0.11 s apart.
        for (let i = 0; i < 4; i++) {
          this.chirp(600 + i * 60, 1200 + i * 80, now + i * 0.11, 0.09, 0.14, 'square');
        }
        break;
      case 'extraBall':
      case 'shootAgain':
        // Extra ball awarded / shoot again: wide rising triangle arpeggio.
        this.arp([523, 784, 1046, 1568], 0.09, 0.35, 0.22, 'triangle');
        break;
      case 'ballSaved':
        // Ball save kicked in: short dip-then-rise square motif.
        this.arp([659, 523, 784], 0.07, 0.2, 0.2);
        break;
      case 'hiscore':
        // New high score: long seven-note triangle fanfare.
        this.arp([784, 1046, 1318, 1568, 2093, 1568, 2093], 0.09, 0.3, 0.18, 'triangle');
        break;
      case 'bonusStart':
        // End-of-ball bonus count begins: three-note square intro.
        this.arp([392, 494, 587], 0.09, 0.2, 0.18);
        break;
      case 'bonusTick':
        // Bonus count-up tick: single 880 Hz square blip (defined but never emitted by rules.js).
        this.osc('square', 880, now, 0.04, 0.12);
        break;
      case 'drain':
        // Ball drained: descending three-note triangle motif plus a 120->60 Hz sine sag.
        this.arp([330, 262, 196], 0.14, 0.35, 0.2, 'triangle');
        this.chirp(120, 60, now + 0.1, 0.4, 0.2, 'sine');
        break;
      case 'drainMB':
        // One multiball ball drained: brief 300->150 Hz triangle droop.
        this.chirp(300, 150, now, 0.2, 0.15, 'triangle');
        break;
      case 'warning':
        // Tilt warning / danger cue: low buzzing 130->110 Hz square.
        this.osc('square', 130, now, 0.18, 0.3, 110);
        break;
      case 'tilt':
        // Tilt: long 110->70 Hz square groan with a 0.6 s low-passed rumble.
        this.osc('square', 110, now, 0.7, 0.35, 70);
        this.noise(now, 0.6, 0.2, 'lowpass', 400, 1);
        break;
      case 'gameOver':
        // Game over: slow descending four-note triangle.
        this.arp([523, 392, 330, 262], 0.16, 0.4, 0.2, 'triangle');
        break;
      case 'uiTick':
        // Attract / menu input tick: tiny 660 Hz sine blip.
        this.osc('sine', 660, now, 0.04, 0.1);
        break;
      case 'kickbackArm':
        // Kickback armed: quick rising 300->900 Hz sine sweep.
        this.chirp(300, 900, now, 0.12, 0.12, 'sine');
        break;
      case 'rampEnter':
        // Ball entering the ramp: short rising 520->1040 Hz triangle whoosh with a soft noise rush.
        this.chirp(520, 1040, now, 0.1, 0.12, 'triangle');
        this.noise(now, 0.09, 0.12, 'bandpass', 1800, 0.8, 3200);
        break;
      case 'bonusX':
        // Bonus multiplier advanced: two-note rising square chime with an octave sparkle.
        this.arp([784, 1046], 0.07, 0.16, 0.16);
        break;
      case 'ballHit':
        // Ball-on-ball impact (multiball): bright metallic click scaled by the impact strength (params.vel).
        this.noise(now, 0.02, 0.12 * velocity, 'bandpass', 3400, 3);
        break;
      default:
        // Unknown names are silently ignored.
        break;
    }
  }
}
